import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useDemo } from "@/store/DemoStore";
import { btn } from "@/components/common";
import { fmtMoney, lineItems } from "@/lib/trip";

const CATEGORIES = ["Cruise fare", "Accommodation", "Flights", "Taxes", "Gratuities", "Transfers", "Excursions", "Experiences", "Amenities", "Promotions", "Insurance", "Agency", "Other"];
const input = "h-10 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";
const num = (v) => Math.max(0, parseFloat(v) || 0);
const newRow = () => ({ key: Math.random().toString(36).slice(2), desc: "", category: "Other", qty: 1, unit: 0 });

function Row({ r, i, onChange, onRemove }) {
  const set = (k) => (e) => onChange({ ...r, [k]: e.target.value });
  return (
    <li className="grid grid-cols-2 gap-2 rounded-xl border border-slate-100 p-3 sm:grid-cols-[1fr_140px_64px_104px_88px_36px] sm:items-center sm:border-0 sm:p-0" data-testid={`invoice-row-${i}`}>
      <input className={`${input} col-span-2 sm:col-span-1`} placeholder="Description" value={r.desc} onChange={set("desc")} data-testid={`invoice-row-desc-${i}`} />
      <select className={`${input} col-span-2 sm:col-span-1`} value={r.category} onChange={set("category")} data-testid={`invoice-row-category-${i}`}>
        {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
      </select>
      <input className={input} type="number" min="0" aria-label="Quantity" value={r.qty} onChange={set("qty")} data-testid={`invoice-row-qty-${i}`} />
      <input className={input} type="number" min="0" step="0.01" aria-label="Unit price" value={r.unit} onChange={set("unit")} data-testid={`invoice-row-unit-${i}`} />
      <p className="self-center text-sm font-semibold text-navy-900 sm:text-right">{fmtMoney(num(r.qty) * num(r.unit), true)}</p>
      <button type="button" onClick={onRemove} aria-label="Remove line item" data-testid={`invoice-row-remove-${i}`} className="grid h-9 w-9 place-items-center justify-self-end rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
    </li>
  );
}

export function InvoiceBuilder({ trip, onDone }) {
  const { updateTrip } = useDemo();
  const [rows, setRows] = useState(() => lineItems(trip).map((r) => ({ ...r, key: Math.random().toString(36).slice(2) })));
  const total = rows.reduce((s, r) => s + num(r.qty) * num(r.unit), 0);

  const save = () => {
    const items = rows.filter((r) => r.desc.trim()).map(({ key, ...r }) => ({ ...r, desc: r.desc.trim(), qty: num(r.qty), unit: num(r.unit) }));
    updateTrip(trip.id, { items });
    toast.success("Invoice saved", { description: `${items.length} line items · ${fmtMoney(items.reduce((s, r) => s + r.qty * r.unit, 0), true)}` });
    onDone();
  };

  return (
    <div data-testid="invoice-builder">
      <div className="hidden grid-cols-[1fr_140px_64px_104px_88px_36px] gap-2 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 sm:grid">
        <span>Description</span><span>Category</span><span>Qty</span><span>Unit price</span><span className="text-right">Total</span><span />
      </div>
      <ul className="space-y-2">
        {rows.map((r, i) => <Row key={r.key} r={r} i={i} onChange={(nr) => setRows(rows.map((x) => (x.key === r.key ? nr : x)))} onRemove={() => setRows(rows.filter((x) => x.key !== r.key))} />)}
      </ul>
      {!rows.length && <p className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-500">No charges yet. Add the first line item to build this invoice.</p>}
      <button type="button" onClick={() => setRows([...rows, newRow()])} data-testid="invoice-add-item-btn" className={`${btn.ghost} mt-3`}><Plus className="h-4 w-4" />Add Line Item</button>
      <div className="mt-3 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-bold text-navy-900">Total <span className="ml-2" data-testid="invoice-builder-total">{fmtMoney(total, true)}</span></p>
        <div className="flex gap-2">
          <button type="button" onClick={onDone} className={btn.secondary} data-testid="invoice-cancel-btn">Cancel</button>
          <button type="button" onClick={save} className={btn.primary} data-testid="invoice-save-btn">Save Invoice</button>
        </div>
      </div>
    </div>
  );
}
